import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PortalPage, EmptyState } from "../../components/portal/PortalPage";
import { AttachmentGallery } from "../../components/portal/AttachmentGallery";
import { usePortal } from "../../components/portal/usePortal";
import { fmtDateTime } from "../../components/portal/portalUtils";
import { Folder, FolderOpen, UploadSimple, Briefcase, Lightning, ClipboardText, CaretRight } from "@phosphor-icons/react";
import { CardListSkeleton } from "../../components/Skeletons";
import { toast } from "sonner";

const KIND_ICON = { job: Briefcase, emergency_job: Lightning, job_request: ClipboardText };

export default function ClientFilesPage() {
  const { folderId } = useParams();
  const { perms } = usePortal();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    setData(null);
    const req = folderId ? API.get(`/folders/${folderId}`) : API.get("/client-hub/files/root");
    req.then(({ data }) => setData(data)).catch((e) => { setData({ error: formatApiError(e?.response?.data?.detail) || "Could not load folder" }); });
  };
  useEffect(load, [folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files?.length || !data?.folder) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData(); fd.append("folder_id", data.folder.id); fd.append("file", f);
      try { await API.post("/files/upload", fd); ok += 1; } catch (e) { toast.error(`${f.name}: ${formatApiError(e?.response?.data?.detail) || "upload failed"}`); }
    }
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
    setUploading(false); load();
  };

  const crumbs = data?.breadcrumb || (data?.folder ? [{ id: data.folder.id, name: data.folder.name }] : []);
  const files = (data?.files || []).map((f) => ({ name: f.name, url: `/api/files/${f.id}/preview`, type: f.mime_type, size: f.size, uploaded_at: f.uploaded_at, by: f.uploaded_by_name }));

  return (
    <PortalPage
      title="File Hub"
      subtitle="Every document, photo and certificate — organised by job."
      testid="client-files"
      actions={perms.can_upload && data?.folder && (
        <>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="files-upload-input" />
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="files-upload-btn"><UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload"}</Button>
        </>
      )}
    >
      <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4 flex-wrap" data-testid="files-breadcrumb">
        <Link to="/portal/files" className="hover:text-primary flex items-center gap-1"><FolderOpen size={14} /> Your files</Link>
        {folderId && crumbs.slice(folderId ? 1 : 0).map((c) => (
          <span key={c.id} className="flex items-center gap-1"><CaretRight size={10} /><Link to={`/portal/files/${c.id}`} className="hover:text-primary">{c.name}</Link></span>
        ))}
      </nav>

      {!data ? <CardListSkeleton rows={4} showHeader={false} /> : data.error ? <EmptyState icon={Folder} title="Folder unavailable" text={data.error} /> : (
        <div className="space-y-6">
          {(data.children || []).length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="files-folders">
              {data.children.map((c) => {
                const Icon = KIND_ICON[c.linked_entity_type] || Folder;
                return (
                  <Link key={c.id} to={`/portal/files/${c.id}`} className="flex items-center gap-3 border border-border rounded-lg bg-card p-3 hover:border-primary/40 card-lift" data-testid={`folder-${c.id}`}>
                    <div className="h-10 w-10 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-600 flex items-center justify-center shrink-0"><Icon size={20} weight="duotone" /></div>
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-[10px] text-muted-foreground capitalize">{(c.linked_entity_type || c.kind || "folder").replace(/_/g, " ")}</p></div>
                  </Link>
                );
              })}
            </div>
          )}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Files ({files.length})</h3>
            {files.length === 0 ? <p className="text-xs text-muted-foreground">No files in this folder{(data.children || []).length ? " — open a sub-folder above." : "."}</p> : (
              <>
                <AttachmentGallery attachments={files} testid="files-gallery" />
                <ul className="mt-3 text-[10px] text-muted-foreground space-y-0.5">
                  {files.slice(0, 20).map((f, i) => <li key={i}>{f.name} · {fmtDateTime(f.uploaded_at)}{f.by ? ` · ${f.by}` : ""}</li>)}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </PortalPage>
  );
}
